import React from "react";
import { Button } from "@/components/ui/button";
import { Sparkles, Play, Layers, BookOpen, Video, Zap, CheckCircle2, LucideIcon } from "lucide-react";
import { useInViewAnimation } from "@/hooks/useInViewAnimation";
import powtoonLogo from "@/assets/powtoon-logo.png";

interface MiniFeature {
  icon: LucideIcon;
  text: string;
}

interface BenefitItem {
  bold?: string;
  text: string;
}

interface HeroSectionProps {
  onStartCreating: () => void;
  onViewExamples?: () => void;
  // Customization props
  badge?: { icon?: LucideIcon; text: string };
  headline?: string;
  highlightedText?: string;
  subheadline?: string;
  description?: string;
  benefits?: BenefitItem[];
  miniFeatures?: MiniFeature[];
  ctaText?: string;
  secondaryCtaText?: string;
  heroVideoUrl?: string | null;
  showCtaIcon?: boolean;
  showSecondaryCtaIcon?: boolean;
}

// Default values for landing page
const defaultMiniFeatures: MiniFeature[] = [
  { icon: Zap, text: "Auto-generates" },
  { icon: Layers, text: "Structures your story" },
  { icon: BookOpen, text: "Scene-by-scene prompts" },
  { icon: Video, text: "Ready for any platform" },
];

// Hardcoded GCS video URL - direct display without DB fetching
const HERO_VIDEO_URL = "https://storage.googleapis.com/prompt-director-base44/video-examples/hero-1765700804984-62b7c5535_AIPromptDirector_CTA1.mp4";

export function HeroSection({ 
  onStartCreating, 
  onViewExamples,
  badge,
  headline = "Unlock a new era of",
  highlightedText = "AI-powered storytelling",
  subheadline = "Master Veo 3 video creation with structured prompting.",
  description = "Transform any idea into precise, Veo 3 optimized prompts that deliver consistent, cinematic results every time.",
  benefits,
  miniFeatures = defaultMiniFeatures,
  ctaText = "Create video",
  secondaryCtaText = "View Examples",
  heroVideoUrl: externalVideoUrl,
  showCtaIcon = true,
  showSecondaryCtaIcon = true,
}: HeroSectionProps) {
  const { ref: heroRef, isInView } = useInViewAnimation({ threshold: 0.1 });

  // Use external URL if provided, otherwise use hardcoded GCS URL
  const heroVideoUrl = externalVideoUrl !== undefined ? externalVideoUrl : HERO_VIDEO_URL;

  return (
    <section 
      ref={heroRef}
      className="relative min-h-[85vh] flex items-center bg-background overflow-hidden"
    >
      {/* Logo Header - Fixed Top Left */}
      <header className="absolute top-0 left-0 right-0 z-20 px-4 sm:px-6 lg:px-8 py-4">
        <div className="max-w-7xl mx-auto">
          <img 
            src={powtoonLogo} 
            alt="Powtoon" 
            className="h-8 w-auto"
          />
        </div>
      </header>

      {/* Subtle background gradient */}
      <div className="absolute inset-0 bg-gradient-to-b from-primary/5 via-transparent to-transparent pointer-events-none" />
      
      <div className="relative z-10 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-16">
        {/* Two Column Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
          
          {/* Left Column - Text Content */}
          <div className="flex flex-col items-center lg:items-start text-center lg:text-left space-y-6">
            
            {/* Badge (if provided) */}
            {badge && (
              <div 
                className={`inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 text-primary text-sm font-medium transition-opacity duration-500 ${
                  isInView ? 'opacity-100 animate-fade-in-up' : 'opacity-0'
                }`}
              >
                {badge.icon && <badge.icon className="w-4 h-4" />}
                {badge.text}
              </div>
            )}

            {/* Main Headline */}
            <h1 
              className={`text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-bold text-foreground leading-[1.1] transition-opacity duration-500 ${
                isInView ? 'opacity-100 animate-fade-in-up' : 'opacity-0'
              }`}
              style={{ animationDelay: '100ms' }}
            >
              {headline}{" "}
              <span className="text-primary">{highlightedText}</span>
            </h1>

            {/* Subheadline */}
            <p 
              className={`text-lg sm:text-xl text-foreground leading-relaxed transition-opacity duration-500 ${
                isInView ? 'opacity-100 animate-fade-in-up' : 'opacity-0'
              }`}
              style={{ animationDelay: '200ms' }}
            >
              {subheadline}
            </p>
            
            {/* Description */}
            <p 
              className={`text-base sm:text-lg text-muted-foreground leading-relaxed transition-opacity duration-500 ${
                isInView ? 'opacity-100 animate-fade-in-up' : 'opacity-0'
              }`}
              style={{ animationDelay: '250ms' }}
            >
              {description}
            </p>

            {/* Benefits List (if provided) */}
            {benefits && benefits.length > 0 && (
              <div 
                className={`space-y-3 transition-opacity duration-500 ${isInView ? 'opacity-100 animate-fade-in-up' : 'opacity-0'}`}
                style={{ animationDelay: '275ms' }}
              >
                {benefits.map((benefit, index) => (
                  <div key={index} className="flex items-center gap-3 text-foreground">
                    <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0" />
                    <span>
                      {benefit.bold && <strong>{benefit.bold}</strong>} {benefit.text}
                    </span>
                  </div>
                ))}
              </div>
            )}

            {/* CTA Buttons */}
            <div 
              className={`flex flex-col sm:flex-row gap-4 pt-4 transition-opacity duration-500 ${
                isInView ? 'opacity-100 animate-fade-in-up' : 'opacity-0'
              }`}
              style={{ animationDelay: '300ms' }}
            >
              <Button
                size="lg"
                onClick={onStartCreating}
                className="px-10 py-7 text-lg font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-0.5"
              >
                {showCtaIcon && <Sparkles className="w-5 h-5 mr-2" />}
                {ctaText}
              </Button>
              {onViewExamples && (
                <Button
                  size="lg"
                  variant="outline"
                  onClick={onViewExamples}
                  className="px-10 py-7 text-lg rounded-xl border-2 hover:bg-primary/5 transition-all duration-300"
                >
                  {showSecondaryCtaIcon && <Play className="w-5 h-5 mr-2" />}
                  {secondaryCtaText}
                </Button>
              )}
            </div>

            {/* Mini Features - below CTA on left side */}
            {miniFeatures && miniFeatures.length > 0 && (
              <div 
                className={`flex flex-wrap justify-center lg:justify-start gap-4 sm:gap-6 pt-6 transition-opacity duration-500 ${
                  isInView ? 'opacity-100 animate-fade-in-up' : 'opacity-0'
                }`}
                style={{ animationDelay: '400ms' }}
              >
                {miniFeatures.map((feature, index) => (
                  <div 
                    key={index} 
                    className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors duration-200"
                  >
                    <div className="w-7 h-7 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <feature.icon className="w-3.5 h-3.5 text-primary" />
                    </div>
                    <span className="font-medium">{feature.text}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Right Column - Video Preview */}
          <div 
            className={`relative transition-opacity duration-500 ${
              isInView ? 'opacity-100 animate-fade-in-up' : 'opacity-0'
            }`}
            style={{ animationDelay: '200ms' }}
          >
            <div className="relative rounded-2xl overflow-hidden shadow-2xl border border-border/50 bg-card">
              {/* Glow effect behind video */}
              <div className="absolute -inset-1 bg-gradient-to-r from-primary/20 via-primary/10 to-primary/20 rounded-2xl blur-xl opacity-50" />
              
              <div className="relative aspect-video">
                {heroVideoUrl ? (
                <video
                  src={heroVideoUrl}
                  autoPlay
                  loop
                  muted
                  playsInline
                    className="w-full h-full object-cover pointer-events-none"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center bg-muted text-muted-foreground">
                    No video uploaded
                  </div>
                )}
              </div>
            </div>

          </div>
        </div>
      </div>
    </section>
  );
}
